import React, { useEffect, useState } from "react";
import { CheckCircleIcon, CloseIcon } from "asset/icons";
import useGetRequest from "hooks/useGetRequest";
import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import { Bars } from "react-loader-spinner";
import { currencies } from "constants/currency";
import capitalizeWords from "utilities/capitalizeWords";
import { errorAlert, successAlert } from "utilities/alerts";
import {
    handleInvoicePayment,
    handleRecordPayment,
    handleRecordShipmentPayment,
} from "api/fetchers";
import { formatMoney } from "utilities/formatMoney";
import currencyPairCheck from "utilities/currencyPairCheck";

const isTradevuWallet = "Received via Tradevu wallet";
const isOtherBank = "Received via other banks";

const RecordPayment = ({ order, orderId, close, totalAmount, refetchActivity, type = "order" }) => {
    const [inputManually, setInputManually] = useState(false);
    const [loading, setLoading] = useState(false);
    const [transaction, setTransaction] = useState();
    const [paymentMethod, setPaymentMethod] = useState("");
    const [currency, setCurrency] = useState("");
    const [amount, setAmount] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [fetchTransactions, transactions, transactionsLoading] = useGetRequest();
    const isInvoiceType = type === "invoice";
    const recordPayment = async () => {
        try {
            setLoading(true);
            if (isInvoiceType) {
                await handleInvoicePayment(orderId, {
                    paymentMethod,
                    currency: currency || order?.currency,
                    amount,
                    paymentDate,
                });
            } else {
                type === "order"
                    ? await handleRecordPayment(orderId, {
                          paymentMethod,
                          currency: currency || order?.currency,
                          amount,
                          paymentDate,
                      })
                    : await handleRecordShipmentPayment(orderId, {
                          paymentMethod,
                          currency: currency || order?.currency,
                          amount,
                          paymentDate,
                      });
            }

            successAlert("Payment recorded.");
            if (refetchActivity && typeof refetchActivity === "function") {
                await refetchActivity?.();
            }
            close();
        } catch (error) {
            errorAlert(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (order?.currency && paymentMethod === isTradevuWallet) {
            fetchTransactions(`/v1/wallets/transactions?currency=${order.currency}`);
        }
    }, [order?.currency, paymentMethod]);
    const minimumAmount = order?.totalAmount - order?.paidAmount;
    const filteredTransactions = transactions?.filter((trxn) => trxn?.totalAmount <= minimumAmount);
    const isInvalidTransaction = amount > minimumAmount;

    return (
        <div
            className="fixed left-0 top-0 h-screen w-screen bg-[#1D29394D] z-[5000] grid place-items-center cursor-auto"
            onClick={(evt) => evt.stopPropagation()}
        >
            <div className="bg-page-bg rounded-2xl w-[95%] sm:w-[560px] !pt-1">
                <h3 className="flex justify-between text-main-dark font-semibold text-[23px] !px-6 !py-4 -mb-1.5">
                    Record a payment
                    <CloseIcon
                        className={`size-6 cursor-pointer translate-x-1 ${
                            loading && "pointer-events-none *:stroke-main-grey/60"
                        }`}
                        onClick={close}
                    />
                </h3>
                <p className="text-[#6B7280] !px-6 font-[380]">
                    Record a payment you've already made for this{" "}
                    {isInvoiceType ? "invoice" : "purchase order"}
                </p>

                <div className="!px-6 !mt-6">
                    {/* Payment Method Dropdown */}
                    <Dropdown
                        value={paymentMethod}
                        onChange={(method) => {
                            setPaymentMethod(method);
                            setInputManually(false); // Reset manual input for other methods
                        }}
                        options={[
                            isTradevuWallet,
                            isOtherBank,
                            "Cash",
                            "Card payment",
                            "Other",
                        ].map((item) => ({ label: item, value: item }))}
                        label="Payment method"
                        labelClassName="!text-[#475467]"
                        placeholder="Select payment method"
                    />

                    {paymentMethod === isTradevuWallet && (
                        <>
                            {inputManually ? (
                                <>
                                    {/* Manual Input Fields */}
                                    <p
                                        className="text-[#475467] font-medium text-[12.5px] !mt-1 cursor-pointer"
                                        onClick={() => setInputManually(false)}
                                    >
                                        Choose from recent transactions
                                    </p>
                                    <div className="flex !gap-2 items-end">
                                        <div className="w-[125px]">
                                            <Dropdown
                                                value={order?.currency || "NGN"}
                                                onChange={() => {}}
                                                options={currencies.map(({ title }) => ({
                                                    label: title,
                                                    value: title,
                                                }))}
                                                label="Amount paid"
                                                labelClassName="!text-[#475467]"
                                                placeholder="NGN"
                                                readOnly
                                            />
                                        </div>

                                        <input
                                            required={true}
                                            placeholder={`Max: ${formatMoney(minimumAmount)}`}
                                            className={
                                                " bg-[#DBDFE5] h-[56px] !pl-2 w-full  placeholder:font-light placeholder:text-sm placeholder:text-main-grey !shadow-[inset_0_0_0_1000px_#EAECF0] rounded-lg"
                                            }
                                            max={totalAmount}
                                            min="1"
                                            formatType="integer"
                                            value={amount}
                                            onChange={(evt) => setAmount(evt.target.value)}
                                            type={"number"}
                                            step="0.1"
                                        />
                                    </div>
                                    <Input
                                        type="date"
                                        onChange={setPaymentDate}
                                        label="Payment date"
                                        labelClassName="!text-[#475467] !mt-5"
                                        min=""
                                        placeholder="Select payment date"
                                        value={paymentDate}
                                    />
                                </>
                            ) : (
                                <>
                                    {/* Recent Transactions Dropdown */}
                                    <p
                                        className="text-[#475467] font-medium text-[12.5px] !mt-1 cursor-pointer"
                                        onClick={() => {
                                            setAmount("");
                                            setCurrency("");
                                            setPaymentDate("");
                                            setInputManually(true);
                                        }}
                                    >
                                        Input manually
                                    </p>
                                    <Dropdown
                                        value={`${
                                            transaction
                                                ? `${currencyPairCheck(
                                                      transaction.currency
                                                  )} ${formatMoney(
                                                      transaction.amount.toFixed(2)
                                                  )} - ${capitalizeWords(
                                                      transaction.accountName
                                                  )} (${transaction.bankName})`
                                                : ""
                                        }`}
                                        options={(transactionsLoading
                                            ? ["loading..."]
                                            : filteredTransactions?.length
                                            ? filteredTransactions
                                            : ["No transactions"]
                                        ).map((item, idx) => ({
                                            label:
                                                typeof item === "string" ? (
                                                    item
                                                ) : (
                                                    <>
                                                        {!idx && (
                                                            <p
                                                                className="text-main-grey font-medium mt-[15px] cursor-text !mb-2 !px-5"
                                                                onClick={(evt) =>
                                                                    evt.stopPropagation()
                                                                }
                                                            >
                                                                Recent transactions
                                                            </p>
                                                        )}
                                                        <p
                                                            className={`flex relative justify-between text-[#475467] font-medium py-[18px] transition-all ${
                                                                item._id === transaction?._id
                                                                    ? "!pl-5 !pr-12"
                                                                    : "!px-5"
                                                            } ${
                                                                idx && "!border-t !border-[#E7E9FB]"
                                                            }`}
                                                            onClick={() => {
                                                                setTransaction(item);
                                                                setAmount(item.amount.toFixed(2));
                                                                setPaymentDate(
                                                                    item.transactionDate
                                                                );
                                                                setCurrency(item.currency);
                                                            }}
                                                        >
                                                            {currencyPairCheck(item.currency)}{" "}
                                                            {formatMoney(item.amount.toFixed(2))}
                                                            <span>
                                                                to{" "}
                                                                {capitalizeWords(item.accountName)}
                                                                <span className="block ml-auto font-normal text-right">
                                                                    {item.bankName}
                                                                </span>
                                                            </span>
                                                            {item._id === transaction?._id && (
                                                                <CheckCircleIcon className="absolute right-4 top-[40%]" />
                                                            )}
                                                        </p>
                                                    </>
                                                ),
                                            value: item?._id,
                                            disabled: typeof item === "string",
                                            className: typeof item !== "string" && "!block !p-0",
                                            action: () => {},
                                        }))}
                                        label="Recent transactions"
                                        labelClassName="!text-[#475467]"
                                        placeholder="Select recent transfers"
                                    />
                                </>
                            )}
                        </>
                    )}

                    {/* Default Manual Input Fields for Other Payment Methods */}
                    {paymentMethod && paymentMethod !== isTradevuWallet && (
                        <>
                            <div className="flex !gap-2 items-end">
                                <div className="w-[125px]">
                                    <Dropdown
                                        value={order?.currency || "..."}
                                        onChange={() => {}}
                                        options={currencies.map(({ title }) => ({
                                            label: title,
                                            value: title,
                                        }))}
                                        label="Amount paid"
                                        labelClassName="!text-[#475467]"
                                        placeholder="NGN"
                                        readOnly
                                    />
                                </div>

                                <input
                                    required={true}
                                    placeholder={`Max: ${minimumAmount}`}
                                    className={
                                        " bg-[#DBDFE5] h-[56px] !pl-2 w-full  placeholder:font-light placeholder:text-sm placeholder:text-main-grey !shadow-[inset_0_0_0_1000px_#EAECF0] rounded-lg"
                                    }
                                    max={minimumAmount}
                                    min="1"
                                    value={amount}
                                    onChange={(evt) => setAmount(evt.target.value)}
                                    type={"number"}
                                    step="0.1"
                                />
                            </div>
                            <Input
                                type="date"
                                onChange={setPaymentDate}
                                label="Payment date"
                                labelClassName="!text-[#475467] !mt-5"
                                min=""
                                max={new Date().toISOString().split("T")[0]}
                                placeholder="Select payment date"
                                value={paymentDate}
                            />
                        </>
                    )}
                </div>

                <div className="flex justify-end items-center !gap-6 !border-t !border-[#E7E9FB] !py-[18px] !px-6 !mt-5">
                    <button
                        className={`bg-[#6F00FF] rounded-lg h-10 w-[115px] text-[#FBFAFF] font-[450] disabled:bg-secondary/30`}
                        disabled={
                            !amount ||
                            amount < 1 ||
                            !paymentMethod ||
                            !paymentDate ||
                            loading ||
                            isInvalidTransaction
                        }
                        onClick={recordPayment}
                    >
                        {loading ? (
                            <Bars
                                color="#fff"
                                height={23}
                                width={23}
                                wrapperClass="w-max mx-auto"
                            />
                        ) : (
                            "Continue"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecordPayment;
